import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow, Button } from "../components/primitives";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";

const inputCls =
  "w-full bg-transparent border-b border-line py-3 text-ink placeholder:text-ink-soft/50 focus:outline-none focus:border-gold transition-colors";

export default function Book() {
  const { data: services = [] } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", preferred_date: "", location: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/booking", { ...form, service: form.service || "General consultation" });
      toast.success(data.message);
      setDone(true);
    } catch {
      toast.error("Could not submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Book Appointment" description="Request a mobile salon appointment with Glam Wagon. Tell us your service, date and location and we'll confirm directly." />
      <section className="pt-40 pb-24 max-w-3xl mx-auto px-6 lg:px-12" data-testid="book-section">
        <Reveal>
          <Eyebrow>Booking request</Eyebrow>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[1] tracking-tight mt-6 text-ink">Book your appointment.</h1>
          <p className="mt-6 text-lg text-ink-soft">Online scheduling is on the way. Share the details below and we'll confirm availability with you personally.</p>
        </Reveal>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="book-success"
            className="mt-12 rounded-3xl border border-line bg-surface p-12 text-center"
          >
            <div className="w-14 h-14 rounded-full bg-gold text-white flex items-center justify-center mx-auto"><Check size={26} /></div>
            <h2 className="font-serif text-3xl mt-6 text-ink">Your request is in.</h2>
            <p className="mt-3 text-ink-soft">We'll be in touch shortly to confirm the details. Thank you for choosing Glam Wagon.</p>
          </motion.div>
        ) : (
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="booking-form" className="mt-12 space-y-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="book-name" />
                <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="book-email" />
                <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="book-phone" />
                <select required value={form.service} onChange={set("service")} className={inputCls} data-testid="book-service">
                  <option value="">Select a service</option>
                  {services.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
                </select>
                <input type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="book-date" />
                <input placeholder="Your location / service area" value={form.location} onChange={set("location")} className={inputCls} data-testid="book-location" />
              </div>
              <textarea rows={3} placeholder="Anything we should know? (optional)" value={form.notes} onChange={set("notes")} className={inputCls} data-testid="book-notes" />
              <Button type="submit" disabled={loading} data-testid="book-submit">
                {loading ? "Sending…" : "Submit request"}
              </Button>
            </form>
          </Reveal>
        )}
      </section>
    </>
  );
}
